import type { AdminCustomerCart } from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import { BaseController, Rx, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { editCartItem, removeCart, removeCartItem } from "../../core/ops/cart-ops";
import {
  CART_CHANGE_FAILED_ON_THE_WAY,
  CART_CHANGE_FAILED_UNREAD,
  CART_DELETE_FAILED_ON_THE_WAY,
  CART_DELETE_FAILED_UNREAD,
  CART_UNREAD,
} from "../../core/primitives/customer-cart";
import { TerminalPaymentController } from "./terminal-payment-controller";

export type CartItem = AdminCustomerCart["items"][number];

export class CustomerCartState {
  readonly busyItemId = new Rx<string | null>(null);
  readonly busyCartId = new Rx<string | null>(null);
  readonly confirmDeleteId = new Rx<string | null>(null);
  readonly deleteError = new Rx<{ id: string; message: string } | null>(null);
  readonly actionError = new Rx<string | null>(null);
  /** The cart the card reader is open for. */
  readonly payingCart = new Rx<AdminCustomerCart | null>(null);
}

export abstract class CustomerCartActions {
  abstract toggleInclude(cart: AdminCustomerCart, item: CartItem): Promise<void>;
  abstract removeItem(cart: AdminCustomerCart, item: CartItem): Promise<void>;
  abstract promptDelete(cart: AdminCustomerCart): void;
  abstract cancelDelete(): void;
  abstract confirmDelete(cart: AdminCustomerCart): Promise<void>;
  abstract takePayment(cart: AdminCustomerCart): void;
}

export type CustomerCartDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "pendingCartsRepository" | "stripeTerminal"
>;

export interface CustomerCartHost {
  /**
   * A cart changed: the page reads it again, since the server decides what an
   * edit did to its checkout. Settles once the page shows that read; true when
   * the cart was read.
   */
  reload(): Promise<boolean>;
}

/** A thrown call, kept to be handled like an answer, then thrown on. */
const kept = (thrown: unknown) => ({ thrown });

/**
 * One customer cart's actions at the counter, wherever its tile shows: the
 * lines in or out of the order, a line or the cart deleted, and payment on a
 * card reader. Shared by the Carts tab and a customer's Checkout. One change at
 * a time, and none while the reader is open; each holds until the cart is read
 * again, whatever it answered, since a refusal can mean the cart was paid or
 * changed elsewhere and a call that threw may have gone through.
 */
export class CustomerCartController extends BaseController implements CustomerCartActions {
  readonly state = new CustomerCartState();
  readonly terminal: TerminalPaymentController;

  constructor(
    subtree: SubtreeModel,
    readonly deps: CustomerCartDeps,
    readonly host: CustomerCartHost,
  ) {
    super();
    subtree.put(CustomerCartState, this.state);
    subtree.put(CustomerCartActions, this);
    this.terminal = this.own(
      new TerminalPaymentController(subtree, deps, {
        onClosed: () => {
          this.state.payingCart.value = null;
          // The webhook marks the cart paid, so it is asked again rather than told; a charge the reader said
          // failed may have gone through too.
          void host.reload();
        },
      }),
    );
  }

  toggleInclude = (cart: AdminCustomerCart, item: CartItem) =>
    this.#itemAction(item.id, "Failed to update item.", () =>
      editCartItem({
        ...this.deps,
        customerCartId: cart.id,
        itemId: item.id,
        edit: { included: !item.includedInPurchaseOrder },
      }),
    );

  removeItem = (cart: AdminCustomerCart, item: CartItem) =>
    this.#itemAction(item.id, "Failed to remove item.", () =>
      removeCartItem({ ...this.deps, customerCartId: cart.id, itemId: item.id }),
    );

  promptDelete = (cart: AdminCustomerCart) => {
    if (this.#paying() || this.#changing()) return;
    this.state.actionError.value = null;
    this.state.deleteError.value = null;
    this.state.confirmDeleteId.value = cart.id;
  };

  cancelDelete = () => {
    this.state.deleteError.value = null;
    this.state.confirmDeleteId.value = null;
  };

  confirmDelete = async (cart: AdminCustomerCart) => {
    if (this.#paying() || this.#changing()) return;
    this.state.busyCartId.value = cart.id;
    this.state.deleteError.value = null;
    try {
      const r = await removeCart({ ...this.deps, customerCartId: cart.id }).catch(kept);
      if ("thrown" in r) {
        if (!this.isDisposed) {
          this.state.confirmDeleteId.value = null;
          // Said after the read, as a change's is.
          const read = await this.host.reload().catch(() => false);
          if (!this.isDisposed)
            this.state.actionError.value = read ? CART_DELETE_FAILED_ON_THE_WAY : CART_DELETE_FAILED_UNREAD;
        }
        throw r.thrown;
      }
      if (this.isDisposed) return;
      if (r.ok) this.state.confirmDeleteId.value = null;
      else this.state.deleteError.value = { id: cart.id, message: "The cart could not be deleted. Please try again." };
      await this.host.reload();
    } finally {
      if (!this.isDisposed) this.state.busyCartId.value = null;
    }
  };

  // Discover and connect as soon as the reader opens: the operator has already
  // said they want to take payment, so a "connect" button first is a step for nothing.
  takePayment = (cart: AdminCustomerCart) => {
    if (this.#paying() || this.#changing()) return;
    this.state.actionError.value = null;
    this.state.payingCart.value = cart;
    void this.terminal.connect();
  };

  /** A change or a payment is on its way: Checkout stays open over it, so what it answers is seen. */
  busy(): boolean {
    return this.#paying() || this.#changing();
  }

  /** The host read the cart: a sentence saying it could not be read is no longer true. */
  cartRead(): void {
    if (CART_UNREAD.includes(this.state.actionError.value ?? "")) this.state.actionError.value = null;
  }

  /** Checkout closed: a question or a sentence from this visit does not greet the next one. */
  reset(): void {
    this.state.confirmDeleteId.value = null;
    this.state.deleteError.value = null;
    this.state.actionError.value = null;
  }

  #paying(): boolean {
    return this.state.payingCart.value !== null;
  }

  #changing(): boolean {
    return this.state.busyItemId.value !== null || this.state.busyCartId.value !== null;
  }

  async #itemAction(itemId: string, failMsg: string, op: () => AsyncResult<unknown, Error>): Promise<void> {
    if (this.#paying() || this.#changing()) return;
    this.state.busyItemId.value = itemId;
    this.state.actionError.value = null;
    try {
      const r = await op().catch(kept);
      if ("thrown" in r) {
        if (!this.isDisposed) {
          // Said after the read, so it does not claim the cart shows what went through when it could not be read.
          const read = await this.host.reload().catch(() => false);
          if (!this.isDisposed)
            this.state.actionError.value = read ? CART_CHANGE_FAILED_ON_THE_WAY : CART_CHANGE_FAILED_UNREAD;
        }
        throw r.thrown;
      }
      if (this.isDisposed) return;
      if (!r.ok) this.state.actionError.value = failMsg;
      await this.host.reload();
    } finally {
      if (!this.isDisposed) this.state.busyItemId.value = null;
    }
  }
}
